import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal } from "./Modal";
import { HeaderModal } from "./Header";
import { FooterModal } from "./Footer";
import close from "assets/images/close-icon.svg";
import Toast from "components/common/Toast";
import MonitorIcon from "@mui/icons-material/Monitor";
import {
  upsertMonitoringSetting,
  getMonitoringSetting,
} from "services/monitoring";

type Frequency = "daily" | "weekly" | "monthly" | "custom";

interface MonitorModalProps {
  isShow: boolean;
  setIsShow: Dispatch<SetStateAction<boolean>>;
  projectId: string;
  level?: "project" | "row";
  rowId?: string | null;
  itemType?: "url" | "patent";
  checkType?: "content_change" | "similar_products" | "examiner_citations";
  teamId?: string;
  onSaved?: () => void;
}

const MonitorModal = ({
  isShow,
  setIsShow,
  projectId,
  level = "project",
  rowId = null,
  itemType,
  checkType,
  teamId = "",
  onSaved,
}: MonitorModalProps) => {
  const [monitoringEnabled, setMonitoringEnabled] = useState<boolean>(false);
  const [frequency, setFrequency] = useState<Frequency>("daily");
  const [customDays, setCustomDays] = useState<number>(3);
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isShow && projectId) {
      fetchExistingSetting();
    }
  }, [isShow, projectId, rowId]);

  const fetchExistingSetting = async () => {
    setLoading(true);
    try {
      const res: any = await getMonitoringSetting(
        projectId,
        rowId || undefined,
        checkType
      );
      if (res) {
        setMonitoringEnabled(res.is_enabled || false);
        setFrequency(res.frequency || "daily");
        if (res.custom_interval_hours) {
          setCustomDays(Math.round(res.custom_interval_hours / 24) || 3);
        }
      }
    } catch {
      // No existing setting — use defaults
    } finally {
      setLoading(false);
    }
  };

  const onCloseModal = () => {
    setIsShow(false);
  };

  const getTeamId = () => {
    if (teamId) return teamId;
    try {
      const selectedTeam = JSON.parse(
        localStorage.getItem("selectedTeam") as string
      );
      return selectedTeam?.currentTeamId || selectedTeam?._id || "";
    } catch {
      return "";
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const basePayload = {
        project_id: projectId,
        team_id: getTeamId(),
        item_id: rowId || undefined,
        item_type: rowId ? itemType : undefined,
        is_enabled: monitoringEnabled,
        frequency,
        custom_interval_hours:
          frequency === "custom" ? customDays * 24 : undefined,
      };

      const resolvedCheckType = checkType || (rowId && itemType === "url" ? "content_change" : "content_change");
      await upsertMonitoringSetting({ ...basePayload, check_type: resolvedCheckType } as any);

      Toast.success(
        "Success",
        monitoringEnabled
          ? `Monitoring enabled (${frequency === "custom" ? `every ${customDays} days` : frequency})`
          : "Monitoring paused"
      );
      onSaved?.();
      setIsShow(false);
    } catch (err) {
      Toast.error("Error", "Failed to update monitoring settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal className={"modal-wrapper max-w-[480px]"} isShow={isShow}>
      <HeaderModal
        title=""
        isShowHeader={true}
        className="modal-header pt-6 px-6"
      >
        <div className="flex items-center justify-between pb-4">
          <div className="flex items-center gap-x-2">
            <MonitorIcon sx={{ width: 32, height: 32, color: "#1751D0" }} />
            <h2 className="text-base text-[#0A090B] font-interSemiBold">
              {level === "project" ? "Monitor Project" : "Monitor Item"}
            </h2>
          </div>
          <img
            className="modal-close modal-toggle cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1"
            src={close}
            onClick={onCloseModal}
            alt="close icon"
          />
        </div>
        <p className="text-sm text-[#4F4D55] font-interRegular pb-2">
          {level === "project"
            ? "Configure default monitoring settings for this project."
            : "Override monitoring settings for this specific item."}
        </p>
      </HeaderModal>

      <div className="px-6 py-4 space-y-5">
        {loading ? (
          <div className="text-center py-4 text-sm text-[#7F7D83]">
            Loading...
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-lg border border-[#E6E6E6] p-4">
              <div>
                <p className="text-sm text-[#0A090B] font-interMedium">
                  Enable monitoring
                </p>
                <p className="text-xs text-[#7F7D83] font-interRegular mt-0.5">
                  {monitoringEnabled
                    ? "Project will be monitored on the schedule below."
                    : "All monitoring is paused."}
                </p>
              </div>
              <button
                onClick={() => setMonitoringEnabled(!monitoringEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  monitoringEnabled ? "bg-[#1751D0]" : "bg-[#D0D5DD]"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    monitoringEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="text-sm text-[#0A090B] font-interMedium block mb-2">
                Frequency
              </label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as Frequency)}
                disabled={!monitoringEnabled}
                className={`w-full border border-[#D0D5DD] rounded-lg px-3 py-2.5 text-sm font-interRegular text-[#0A090B] outline-none focus:border-[#1751D0] ${
                  !monitoringEnabled
                    ? "bg-[#F9FAFB] text-[#ADACB0] cursor-not-allowed"
                    : "bg-white"
                }`}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            {frequency === "custom" && (
              <div>
                <label className="text-sm text-[#0A090B] font-interMedium block mb-2">
                  Every how many days?
                </label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={customDays}
                  onChange={(e) =>
                    setCustomDays(Math.max(1, Number(e.target.value) || 1))
                  }
                  disabled={!monitoringEnabled}
                  className={`w-full border border-[#D0D5DD] rounded-lg px-3 py-2.5 text-sm font-interRegular text-[#0A090B] outline-none focus:border-[#1751D0] ${
                    !monitoringEnabled
                      ? "bg-[#F9FAFB] text-[#ADACB0] cursor-not-allowed"
                      : "bg-white"
                  }`}
                />
              </div>
            )}
          </>
        )}
      </div>

      <FooterModal
        title=""
        isShowFooter={true}
        className="modal-footer pt-3 px-6 pb-6"
      >
        <div className="flex items-center gap-x-3 justify-center">
          <button
            onClick={onCloseModal}
            className="text-[#4F4D55] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-2.5 !text-base flex-1 hover:bg-[#f2f4f7]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="!text-base text-white font-interSemiBold bg-[#1751D0] hover:bg-[#113B98] rounded-lg px-3.5 py-2.5 flex-1 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </FooterModal>
    </Modal>
  );
};

export default MonitorModal;
